import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import { btn, btnGhost, card, errMsg, field, Provenance } from "./ui";

const EMPTY = { name: "", email: "", phone: "", linkedin_url: "", status: "active", source: "human", notes: "" };

export default function StaffingCandidates() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [source, setSource] = useState("");
    const [form, setForm] = useState(EMPTY);
    const [csv, setCsv] = useState("name,email,phone,linkedin_url,notes\n");
    const [importPreview, setImportPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (status) params.status = status;
        if (source.trim()) params.source = source.trim();
        return api.get("/ats-core/candidates", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/ats-core/candidates", form);
            setForm(EMPTY);
            toast.success("Kandidaat opgeslagen in Jeremai");
            await load();
        } catch (err) {
            toast.error(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    const dryRun = async () => {
        try {
            const r = await api.post("/ats-core/import/recruit-crm", { csv_text: csv, dry_run: true });
            setImportPreview(r.data);
            toast.success(`Dry-run: ${r.data.would_insert} rijen, niets geschreven naar Recruit CRM`);
        } catch (err) {
            toast.error(errMsg(err));
        }
    };

    return (
        <PageHeader title="Kandidaten" subtitle="Kernvelden in Jeremai. Zoeken en filteren op status en bron.">
            <form onSubmit={create} className={`${card} grid md:grid-cols-3 gap-3 mb-6`} data-testid="candidate-form">
                <input className={field} required placeholder="Naam" value={form.name} data-testid="candidate-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className={field} placeholder="E-mail" value={form.email} data-testid="candidate-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className={field} placeholder="Telefoon" value={form.phone} data-testid="candidate-phone"
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input className={field} placeholder="LinkedIn URL" value={form.linkedin_url}
                    onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
                <select className={field} value={form.status} data-testid="candidate-status"
                    onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {["active", "passive", "placed", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className={field} placeholder="Bron" value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })} />
                <textarea className={`${field} md:col-span-2`} rows={2} placeholder="Notities" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <button className={btn} disabled={busy} data-testid="candidate-save">Opslaan</button>
            </form>

            <div className="flex flex-wrap gap-2 mb-4">
                <input className={`${field} max-w-xs`} placeholder="Zoek naam, e-mail, notitie" value={q}
                    data-testid="candidate-search" onChange={(e) => setQ(e.target.value)} />
                <select className={`${field} max-w-[160px]`} value={status} data-testid="candidate-filter-status"
                    onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Alle statussen</option>
                    {["active", "passive", "placed", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className={`${field} max-w-[180px]`} placeholder="Filter bron" value={source}
                    onChange={(e) => setSource(e.target.value)} />
                <button className={btnGhost} onClick={load} data-testid="candidate-filter-apply">Filter</button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/8" data-testid="candidate-table">
                <table className="w-full text-sm">
                    <thead className="text-left text-[var(--jr-text-3)] text-xs uppercase tracking-wider">
                        <tr>
                            {["Naam", "E-mail", "Telefoon", "Status", "Bron", "Herkomst"].map((h) => (
                                <th key={h} className="px-3 py-2 font-medium">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr key={c.id} className="border-t border-white/8" data-testid={`candidate-row-${c.id}`}>
                                <td className="px-3 py-2 text-white">
                                    {c.name}
                                    {c.notes ? <div className="text-xs text-[var(--jr-text-3)] mt-0.5">{c.notes}</div> : null}
                                </td>
                                <td className="px-3 py-2 text-[var(--jr-text-2)]">{c.email || "—"}</td>
                                <td className="px-3 py-2 text-[var(--jr-text-2)]">{c.phone || "—"}</td>
                                <td className="px-3 py-2">{c.status || "active"}</td>
                                <td className="px-3 py-2 text-[var(--jr-text-2)]">{c.source || "—"}</td>
                                <td className="px-3 py-2"><Provenance row={c} /></td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan={6} className="px-3 py-6 text-[var(--jr-text-3)]">Geen kandidaten.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={`${card} mt-6`}>
                <div className="text-sm text-white mb-1">Recruit CRM import — parallel-run</div>
                <p className="text-xs text-[var(--jr-text-2)] mb-3">
                    Dry-run schrijft niets buiten Jeremai en is geen cutover. Plak een CSV met kolom name.
                </p>
                <textarea className={field} rows={4} value={csv} data-testid="import-csv" onChange={(e) => setCsv(e.target.value)} />
                <button className={`${btnGhost} mt-3`} onClick={dryRun} data-testid="import-dry-run">Dry-run</button>
                {importPreview && (
                    <p className="mt-2 text-xs text-[var(--jr-text-2)]" data-testid="import-result">
                        {importPreview.would_insert} rijen · mode {importPreview.migration_mode} · externe SoR-write: {String(importPreview.writes_external_sor)}
                    </p>
                )}
            </div>
        </PageHeader>
    );
}
